/**
 * Copyright © 2025-2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from './store/rootStore';
import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { colors } from '@farmlands/mui-theme';
import { ShadcnThemeProvider } from '@farmlands/shadcn-ui';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { BrowserRouter } from 'react-router-dom';

import { Loader } from './components/Loader';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from './util/useToast';
import { OnlineStatusProvider } from './util/useOnlineStatus';
import { useServiceWorker } from './useServiceWorker';
import './native/utils/SafeArea';
import './services/firebase';
import './services/sentry';
import { AppRoutes } from './components/AppRouter';
import { PreviousPageProvider } from './util/usePreviousPage';
import { RemoteConfigProvider } from './services/firebase/remote-config';
import PushNotificationsProvider from './services/push-notifications/PushNotificationsProvider';
import { AdobeRouter } from './services/adobe/AdobeRouter.tsx/AdobeRouter';
import { farmlandsMuiTheme } from './theme/farmlands-mui-theme';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false } } });

// Hide the splash (you should do this on app launch)
function App() {
  if (Capacitor.isNativePlatform()) {
    SplashScreen.hide();
  } else if (!Capacitor.isNativePlatform()) {
    const { waitingWorker, showReload, reloadPage } = useServiceWorker();
    useEffect(() => {
      if (showReload && waitingWorker) {
        toast.onChange((e) => {
          if (e.id === 'reload' && e.status === 'removed') {
            reloadPage();
          }
        });
        toast('Reload for latest version', {
          autoClose: false,
          toastId: 'reload',
          closeButton: (
            // Must be manually styled as it sits outside of MuiTheme context.
            <button onClick={() => reloadPage()} className='toast-refresh-button'>
              Refresh
            </button>
          ),
        });
      }
    }, [waitingWorker, showReload, reloadPage]);
  }

  return (
    <BrowserRouter>
      <ShadcnThemeProvider theme='verdure'>
        <ThemeProvider theme={farmlandsMuiTheme()}>
          <Provider store={store}>
            <PersistGate
              persistor={persistor}
              loading={
                Capacitor.isNativePlatform() ? (
                  <Box width='100%' height='100%' bgcolor={colors.primary700}>
                    <Loader title='Launching' />
                  </Box>
                ) : (
                  <Loader title='Launching' />
                )
              }
            >
              <PreviousPageProvider>
                <PushNotificationsProvider>
                  <OnlineStatusProvider>
                    <RemoteConfigProvider>
                      <ToastContainer />
                      <CssBaseline />
                      <QueryClientProvider client={queryClient}>
                        <AdobeRouter>
                          <AppRoutes />
                        </AdobeRouter>
                      </QueryClientProvider>
                    </RemoteConfigProvider>
                  </OnlineStatusProvider>
                </PushNotificationsProvider>
              </PreviousPageProvider>
            </PersistGate>
          </Provider>
        </ThemeProvider>
      </ShadcnThemeProvider>
    </BrowserRouter>
  );
}

export default App;
